import React, { useState, useEffect } from 'react';
import '../ShareModal.css';
import { useShareSchedule } from '../../api/schedules';
import { useNotifications } from '../../context/NotificationContext';
import { Copy } from 'lucide-react';

interface ShareScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleUuid: string | null;
  scheduleName?: string;
}

const ShareScheduleModal: React.FC<ShareScheduleModalProps> = ({ isOpen, onClose, scheduleUuid, scheduleName }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string>('');

  const shareScheduleMutation = useShareSchedule();
  const { showError } = useNotifications();

  useEffect(() => {
    if (isOpen) {
      if (!scheduleUuid) {
        showError('No schedule selected', new Error('Schedule is required'));
        return;
      }
      shareScheduleMutation.mutate(scheduleUuid, {
        onSuccess: (data) => {
          const url = `${window.location.origin}/schedule/invite/${scheduleUuid}?share_id=${data.share_uuid}`;
          setShareUrl(url);
        },
        onError: (error) => {
          showError('Failed to generate share link. Please try again.', error);
        },
      });
    }
  }, [isOpen]);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h2 className="modal-title">Share {scheduleName ? `"${scheduleName}"` : 'Your Schedule'}</h2>

        {/* Direct Link Sharing */}
        <div className="share-section">
          <h3>Direct Link</h3>
          <p>Share a link that others can use to view your schedule</p>
          <div className="input-group">
            <input
              type="text"
              value={shareUrl || 'Generating link...'}
              readOnly
              disabled={!shareUrl}
            />
          </div>
          <button
            className="btn btn--secondary btn--block"
            onClick={() => copyToClipboard(shareUrl, 'url')}
            disabled={!shareUrl || shareScheduleMutation.isPending}
          >
            <Copy className="icon icon--sm" />{shareScheduleMutation.isPending ? 'Loading...' : copied === 'url' ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
        <div className="security-info">
          <p>For added security, only registered users who you have shared the link with can view your schedule.</p>
        </div>
      </div>
    </div>
  );
};

export default ShareScheduleModal;
